/**
 * Runtime konfigurace Phoenix frontendu fetchovaná z backendu (`/api/config/runtime`).
 *
 * Důvod: hodnoty se musí dát měnit per-deployment bez rebuildu Vite bundle.
 * `import.meta.env.VITE_*` se inlinuje při buildu a multi-tenant nasazení (různí zákazníci,
 * každý má jinou Magic URL) by jinak vyžadovalo build na cílového zákazníka.
 *
 * Loader se volá jednou při bootstrapu v [main.tsx](../../main.tsx) PŘED renderem React stromu.
 * Po té všechny komponenty čtou přes `getRuntimeConfig()` synchronně z modulové cache.
 */

export interface LegacyMagicRuntimeConfig {
  readonly baseUrl: string;
  readonly phoenixOnTop: boolean;
}

export interface UiRuntimeConfig {
  /** Titulek do `document.title`. Pokud je prázdný, ponechá se hodnota z index.html. */
  readonly title: string;
}

export interface AppRuntimeConfig {
  /** Verze Phoenix aplikace (patička LoginGate). Prázdné = nezobrazovat. */
  readonly version: string;
  /** Prostředí pro badge: `prod` | `test` | `dev`. Prázdné = nezobrazovat. */
  readonly environment: string;
}

export interface RuntimeConfig {
  readonly legacyMagic: LegacyMagicRuntimeConfig;
  readonly ui: UiRuntimeConfig;
  readonly app: AppRuntimeConfig;
}

interface RuntimeConfigResponseDto {
  readonly legacyMagic?: {
    readonly baseUrl?: string;
    readonly phoenixOnTop?: boolean;
  };
  readonly ui?: {
    readonly title?: string;
  };
  readonly app?: {
    readonly version?: string;
    readonly environment?: string;
  };
}

const EMPTY_CONFIG: RuntimeConfig = Object.freeze({
  legacyMagic: Object.freeze({ baseUrl: '', phoenixOnTop: false }),
  ui: Object.freeze({ title: '' }),
  app: Object.freeze({ version: '', environment: '' }),
});

let cachedConfig: RuntimeConfig = EMPTY_CONFIG;
let loaded = false;

/**
 * Fetchne konfiguraci z backendu a uloží do modulové cache. Vola se v `main.tsx`
 * před renderem; pokud selže, frontend pokračuje s prázdnou konfigurací (Phoenix-On-Top režim
 * efektivně vypnutý). Backend endpoint je anonymní (`AllowAnonymous`).
 */
export async function loadRuntimeConfig(): Promise<void> {
  try {
    const response = await fetch('/api/config/runtime', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      console.warn('[runtimeConfig] fetch failed with status', response.status);
      return;
    }
    const dto = (await response.json()) as RuntimeConfigResponseDto;
    cachedConfig = Object.freeze({
      legacyMagic: Object.freeze({
        baseUrl: dto.legacyMagic?.baseUrl ?? '',
        phoenixOnTop: dto.legacyMagic?.phoenixOnTop ?? false,
      }),
      ui: Object.freeze({
        title: dto.ui?.title ?? '',
      }),
      app: Object.freeze({
        version: dto.app?.version ?? '',
        environment: dto.app?.environment ?? '',
      }),
    });
  } catch (e) {
    console.warn('[runtimeConfig] fetch threw', e);
  } finally {
    loaded = true;
  }
}

/**
 * Synchronní přístup k načtené konfiguraci. Bezpečné volat až po `loadRuntimeConfig()`;
 * před tím vrací default prázdnou konfiguraci (nešťastné, ale neselže).
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (!loaded && import.meta.env.DEV) {
    console.warn(
      '[runtimeConfig] getRuntimeConfig() called before loadRuntimeConfig() — returning empty defaults'
    );
  }
  return cachedConfig;
}

/** Pouze pro testy — resetuje cache do prázdného stavu. */
export function __resetRuntimeConfigForTests(): void {
  cachedConfig = EMPTY_CONFIG;
  loaded = false;
}
